import { VehicleTechTuningItemConfig } from '~/rage/services/TechTuningService';
import { Gender, TechTuningCategory } from '~/shared';
import { IShopItem } from '../../components/ListTab/ListTab.model';
import { createSlice } from '../wrappers';

export enum StageTypes {
	Category = 'category',
	List = 'list',
	Handling = 'handling',
}

interface TuningShopSlice {
	shopItems: string[];
	ownedItems: string[];
	wearingItems: string[];
	activeItemId: string;
	activeStage: StageTypes;
	activeCategory: TechTuningCategory | null;
	maxCategoryIndex: number;
	activeList: VehicleTechTuningItemConfig[];
	activeListIndex: number | null;
	maxListIndex: number;
	tabIndex: number;
	maxTabIndex: number;
	inventoryEmpty: boolean;
	playerGender: Gender;
	helpHoverCategory: TechTuningCategory | null;
	draggedItem: VehicleTechTuningItemConfig | null;
	dragSource: 'owned' | 'equipped' | null;
	installedItems: {
		[TechTuningCategory.ENGINE]: null | IShopItem;
		[TechTuningCategory.TURBO]: null | IShopItem;
		[TechTuningCategory.EXHAUST]: null | IShopItem;
		[TechTuningCategory.RADIATOR]: null | IShopItem;
		[TechTuningCategory.STARTER]: null | IShopItem;
		[TechTuningCategory.BRAKES]: null | IShopItem;
		[TechTuningCategory.SUSPENSION]: null | IShopItem;
		[TechTuningCategory.TIRES]: null | IShopItem;
	};
	casesOverlayVisible: boolean;
	caseResultVisible: boolean;
	searchValue: string;
	caseResultItem: string | null;
	menuTabsLocked: boolean;
}

const initialState: TuningShopSlice = {
	shopItems: [],
	ownedItems: [],
	wearingItems: [],
	activeItemId: '',
	activeStage: StageTypes.Category,
	activeCategory: null,
	maxCategoryIndex: 0,
	activeList: [] as VehicleTechTuningItemConfig[],
	activeListIndex: null,
	maxListIndex: 0,
	tabIndex: 0,
	maxTabIndex: 1,
	inventoryEmpty: true,
	playerGender: Gender.MALE,
	helpHoverCategory: null,
	draggedItem: null,
	dragSource: null,
	installedItems: {
		[TechTuningCategory.ENGINE]: null,
		[TechTuningCategory.TURBO]: null,
		[TechTuningCategory.EXHAUST]: null,
		[TechTuningCategory.RADIATOR]: null,
		[TechTuningCategory.STARTER]: null,
		[TechTuningCategory.BRAKES]: null,
		[TechTuningCategory.SUSPENSION]: null,
		[TechTuningCategory.TIRES]: null,
	},
	casesOverlayVisible: false,
	caseResultVisible: false,
	searchValue: '',
	caseResultItem: null,
	menuTabsLocked: false,
};

const clothesShopSlice = createSlice({
	name: 'clothesShop',
	initialState,
	reducers: {
		setActiveStage: (state, action) => {
			state.activeStage = action.payload;
		},
		setActiveListIndex: (state, action) => {
			state.activeListIndex = action.payload;
		},
		setMaxListIndex: (state, action) => {
			state.maxListIndex = action.payload;
		},
		setActiveList: (state, action) => {
			state.activeList = action.payload;
		},
		setActiveCategory: (state, action) => {
			state.activeCategory = action.payload;
		},
		setMaxCategoryIndex: (state, action) => {
			state.maxCategoryIndex = action.payload;
		},
		setTabIndex: (state, action) => {
			state.tabIndex = action.payload;
		},
		setTabIndexPrev: (state) => {
			if (
				state.menuTabsLocked ||
				state.caseResultVisible ||
				state.casesOverlayVisible
			) {
				return;
			}
			const index = state.tabIndex;
			state.tabIndex = index > 0 ? index - 1 : index;
		},
		setTabIndexNext: (state) => {
			if (
				state.menuTabsLocked ||
				state.caseResultVisible ||
				state.casesOverlayVisible
			) {
				return;
			}
			const index = state.tabIndex;
			const maxIndex = state.maxTabIndex;
			state.tabIndex = index < maxIndex ? index + 1 : index;
		},
		setShopItems: (state, action) => {
			state.shopItems = action.payload;
		},
		setOwnedItems: (state, action) => {
			state.ownedItems = action.payload;
		},
		setWearingItems: (state, action) => {
			state.wearingItems = action.payload;
		},
		setActiveItemId: (state, action) => {
			state.activeItemId = action.payload;
		},
		setPlayerGender: (state, action) => {
			state.playerGender = action.payload;
		},
		setHelpHoverCategory: (state, action) => {
			state.helpHoverCategory = action.payload;
		},
		setDraggedItem: (state, action) => {
			state.draggedItem = action.payload;
		},
		setDragSource: (state, action) => {
			state.dragSource = action.payload;
		},
		setInstalledItems: (state, action) => {
			state.installedItems = action.payload;
		},
		setCasesOverlayVisible: (state, action) => {
			state.casesOverlayVisible = action.payload;
		},
		setCaseResultVisible: (state, action) => {
			state.caseResultVisible = action.payload;
		},
		setSearchValue: (state, action) => {
			state.searchValue = action.payload;
		},
		setCaseResultItem: (state, action) => {
			state.caseResultItem = action.payload;
		},
		setMenuTabsLocked: (state, action) => {
			state.menuTabsLocked = action.payload;
		},
	},
});

export const {
	setActiveStage,
	setActiveListIndex,
	setMaxListIndex,
	setActiveList,
	setActiveCategory,
	setMaxCategoryIndex,
	setTabIndex,
	setTabIndexPrev,
	setTabIndexNext,
	setShopItems,
	setOwnedItems,
	setWearingItems,
	setActiveItemId,
	setPlayerGender,
	setHelpHoverCategory,
	setDraggedItem,
	setDragSource,
	setInstalledItems,
	setSearchValue,
	setCasesOverlayVisible,
	setCaseResultVisible,
	setCaseResultItem,
	setMenuTabsLocked,
} = clothesShopSlice.actions;

export default clothesShopSlice.reducer;
